import React, {Component} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {Button, Col, Collapse, Form, Input, InputNumber, notification, Row} from 'antd';

import {packageProduct} from './actions';
import {getDescriptionFromError} from "../utils/errors";
import {formItemLayout} from "../utils/style";
import {bindActionCreators} from "redux";
import {generateBarcode, getProductStocks, searchProducts} from "../Product/actions";
import MoneyDisplay from "../common/MoneyDisplay";
import ProductAutoComplete from "../Product/ProductAutoComplete";
import {GramsPerWeightUnit, MilliLitresPerVolumeUnit} from "../Product/units";
import NormalisedQuantityDisplay from "./NormalisedQuantityDisplay";

const FormItem = Form.Item;
const ButtonGroup = Button.Group;
const Panel = Collapse.Panel;

const NormalisedUnitFactor = Object.freeze({
  ...GramsPerWeightUnit,
  ...MilliLitresPerVolumeUnit,
  'each': 1,
});

function calculateNormalisedQuantity(product, quantity) {
  if (!product || !quantity) return null;
  const conversionRate = NormalisedUnitFactor[product.unit];
  if (!conversionRate) return null;
  return (quantity * product.packageQuantity) * conversionRate;
}

function hasErrors(fieldsError) {
  return Object.keys(fieldsError).some(field => fieldsError[field]);
}

class PackageProductForm extends Component {
  static propTypes = {
    form: PropTypes.object.isRequired,

    loading: PropTypes.bool.isRequired,
    dispensaryId: PropTypes.string.isRequired,
    products: PropTypes.array.isRequired,
    bulkConsignment: PropTypes.object.isRequired,
    generatedBarcode: PropTypes.string,
    productStocks: PropTypes.array.isRequired,
    isExpanded: PropTypes.bool.isRequired,

    packageProduct: PropTypes.func.isRequired,
    productPackaged: PropTypes.func.isRequired,
    generateBarcode: PropTypes.func.isRequired,
    getProductStocks: PropTypes.func.isRequired,
    searchProducts: PropTypes.func.isRequired,
  };

  handleSubmit = (e) => {
    const {bulkConsignment: {bulkConsignmentId}} = this.props;
    e.preventDefault();
    this.props.form.validateFields(async(err, values) => {
      if (err) return;
      try {
        await this.props.packageProduct({...values, bulkConsignmentId});
        notification.success({
          message: 'Success',
          description: 'Product was packaged',
        });
        this.props.form.setFieldsValue({quantity: null});
        this.props.productPackaged();
      } catch (err) {
        notification.error({
          message: 'Failed',
          description: getDescriptionFromError(err)
        });
      }
    });
  };

  handleCancel = () => {
    this.props.form.resetFields();
  };

  handleGenerateClick = async() => {
    const {dispensaryId, generateBarcode, form: {setFieldsValue}} = this.props;
    await generateBarcode({dispensaryId});
    const {generatedBarcode} = this.props;
    setFieldsValue({sku: generatedBarcode});
  };

  onProductChange = async(value) => {
    const {form, products, bulkConsignment} = this.props;
    const product = products.find(({productId}) => productId === value);
    if (product) {
      const quantityPerUnit = calculateNormalisedQuantity(product, 1);
      const {quantity, netCost} = bulkConsignment;
      const netCostPerUnit = quantityPerUnit * netCost / quantity;
      let sku = null;

      try {
        await this.props.getProductStocks({productId: value});
        const desiredLotNumber = this.props.bulkConsignment.lotNumber;
        const productStock = this.props.productStocks.find(({lotNumber}) => lotNumber === desiredLotNumber);
        if (productStock) {
          sku = productStock.sku;
        } else {
          notification.info({
            message: "No SKU Found",
            description: `No SKU for '${product.name}' with Lot Number ${desiredLotNumber}`
          });
        }
      } catch (err) {
        notification.error({
          message: 'Failed to lookup SKU',
          description: getDescriptionFromError(err)
        });
      }

      form.setFieldsValue({netCostPerUnit, sku});
    }
  };

  onProductSearch = (value) => {
    const {searchProducts, dispensaryId} = this.props;
    searchProducts({pagination: {current: 1, pageSize: 25}, filters: {dispensaryId}, searchFilter: value});
  };

  render() {
    const {form: {getFieldDecorator, getFieldsError, getFieldValue}, loading, products, isExpanded, bulkConsignment} = this.props;
    const quantity = getFieldValue('quantity');
    const product = products.find(({productId}) => productId === getFieldValue('productId'));
    const netCostPerUnit = getFieldValue('netCostPerUnit');
    const normalisedQuantity = calculateNormalisedQuantity(product, quantity);
    const netCost = netCostPerUnit * quantity;
    const enoughRemainingQuantity = (normalisedQuantity && bulkConsignment) ? normalisedQuantity <= bulkConsignment.remainingQuantity : true;

    return (
      <Collapse bordered={false} defaultActiveKey={isExpanded ? ['1'] : null}><Panel header='Package Product' key='1'>
        <Form onSubmit={this.handleSubmit}>
          <FormItem
            {...formItemLayout}
            label="Product"
          >
            {getFieldDecorator('productId', {
              rules: [{
                required: true,
                message: 'Product is required'
              }]
            })(
              <ProductAutoComplete onSearch={this.onProductSearch} products={products} onChange={this.onProductChange}/>
            )}
          </FormItem>
          <FormItem
            {...formItemLayout}
            label="Net Cost Per Unit"
          >
            {getFieldDecorator('netCostPerUnit', {
              rules: [{
                required: true,
                message: 'Net Cost Per Unit is required'
              }, {
                type: 'number',
                min: 0,
                message: 'Must be at least 0'
              }]
            })(
              <InputNumber/>
            )}
          </FormItem>
          <FormItem
            {...formItemLayout}
            label="SKU"
          >
            <Row gutter={16}>
              <Col span={16}>
                {getFieldDecorator('sku', {
                  rules: [{required: true, message: 'SKU is required'}]
                })(
                  <Input/>
                )}
              </Col>
              <Col span={8}><Button onClick={this.handleGenerateClick}>Generate</Button></Col>
            </Row>
          </FormItem>
          <FormItem
            {...formItemLayout}
            label="Quantity"
            validateStatus={enoughRemainingQuantity ? "success" : "error"}
            help={enoughRemainingQuantity ? "" : "Insufficient remaining quantity"}
          >
            {getFieldDecorator('quantity', {
              rules: [{
                required: true,
                message: 'Quantity is required'
              }, {
                type: 'number',
                min: 1,
                message: 'Must be at least 1'
              }]
            })(
              <InputNumber precision={0}/>
            )}
          </FormItem>
          <FormItem
            {...formItemLayout}
            label="Bulk Qty / Net Cost"
          >
            {normalisedQuantity && <NormalisedQuantityDisplay normalisedQuantity={normalisedQuantity} unit={product.unit}/>}
            {normalisedQuantity && !isNaN(netCost) && netCost && ' / '}
            {!isNaN(netCost) && netCost && <MoneyDisplay amount={netCost} decimals={5}/>}
          </FormItem>
          <FormItem>
            <ButtonGroup>
              <Button
                type="primary"
                htmlType="submit"
                disabled={loading || hasErrors(getFieldsError()) || !enoughRemainingQuantity}
              >
                Package Product
              </Button>
              <Button
                htmlType="button"
                onClick={this.handleCancel}
              >
                Cancel
              </Button>
            </ButtonGroup>
          </FormItem>
        </Form>
      </Panel></Collapse>
    );
  }
}

const mapStateToProps = state => ({
  loading: state.product.productsLoading || state.bulkConsignment.packagingProduct || state.product.gettingProductStocks,
  dispensaryId: state.dispensary.selectedDispensaryId,
  products: state.product.productsSearch.results,
  generatedBarcode: state.product.generatedBarcode,
  productStocks: state.product.productStocks,
});

const mapDispatchToProps = dispatch => (bindActionCreators({
  packageProduct,
  generateBarcode,
  getProductStocks,
  searchProducts,
}, dispatch));

export default connect(mapStateToProps, mapDispatchToProps)(Form.create()(PackageProductForm));



// WEBPACK FOOTER //
// ./src/BulkConsignment/PackageProductForm.js